import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';
import { isMobile } from 'react-device-detect';

import './StakeInput.scss';
import { RootState } from '../../configs/store';
import VirtualKeyboardContext from '../VirtualKeyboard/Context/VirtualKeyboard.context';

interface StakeInputProps {
  defaultValue: string,
  name: string,
  className?: string,
  disabled?: boolean,
  onChange?: Function,
  onFocus?: Function,
  /** If  Left Blank will only get to previous state*/
  onBlur?: Function,
  /**
   * Values expected: 'sports.inPlay' | 'sports.single' | 'sports.acca' | 'sports.system' |
        'racing.tote' | 'racing.fixedOdds' | 'racing.acca' |
        'lotto.single' | 'lotto.acca'
   */
  rule: string,
  type: 'bet' | 'betslip',
  /**
   * Whether or not we want to use debounce on change event
   */
  useDebounce?: boolean
}

export default function StakeInput({
  defaultValue,
  name,
  className,
  onChange,
  onFocus,
  onBlur,
  rule,
  type,
  disabled = false,
  useDebounce = true
}: StakeInputProps) {
  const splitType = rule.split('.');

  const { stakingRules } = useSelector((state: RootState) => state.content);
  const rules = stakingRules[splitType[0]][splitType[1]];

  const [previousValue, setPreviousValue] = useState<string>('0');
  const [value, setValue] = useState<string>(defaultValue);
  const [showError, setShowError] = useState<boolean>(false);

  const { showKeyboard, setShowKeyboard, setSelectedInput, selectedInput } = useContext(VirtualKeyboardContext);
  const inputRef = useRef(null);

  const handleFocus = () => {
    setValue('');
    if (value !== '') {
      setPreviousValue(value);
    }

    if (!showKeyboard && setShowKeyboard) {
      setShowKeyboard(true);
    }

    if (!_.isNull(inputRef.current)) {
      setSelectedInput(inputRef.current);
    }

    if (onFocus) {
      onFocus(name, value);
    }
  };

  const handleBlur = () => {
    if (!showKeyboard) {
      if (value === '') {
        setValue(parseFloat(previousValue).toFixed(2));

        if (onBlur) {
          onBlur(name, previousValue);
        }
      } else {
        setValue(parseFloat(value).toFixed(2));

        if (onBlur) {
          onBlur(name, value);
        }
      }
    }
  };

  const debouncedOnChange = useCallback(_.debounce((value) => {
    onChange(name, value);
  }, 1000), []);

  const handleChange = (e) => {
    const val = e.target.value === '' ? '0' : e.target.value;
    setValue(val);

    if (onChange) {
      if (useDebounce) {
        debouncedOnChange(val);
      } else {
        onChange(name, val);
      }
    }
  };

  useEffect(() => {
    if (!showKeyboard) {
      setValue(parseFloat(defaultValue).toFixed(2));
    } else {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    setValue(parseFloat(value).toFixed(2));
  }, []);

  useEffect(() => {
    if (type === 'bet' && value !== '') {
      setShowError(value < rules.minimum || value > rules.maximum);
    }

    if (type === 'betslip' && value !== '') {
      setShowError(value < rules.minBetslip || value > rules.maxBetslip);
    }
  }, [value]);

  useEffect(() => {
    handleBlur();
  }, [showKeyboard]);

  return (
    <>
      <input
        ref={ inputRef }
        type="string"
        name={ name }
        onFocus={ handleFocus }
        onBlur={ handleBlur }
        value={ value }
        placeholder={ previousValue }
        onChange={ handleChange }
        className={
          classNames(
            'stake-input',
            className,
            {
              'stake-input--error': showError,
              'stake-input--selected': _.isEqual(selectedInput, inputRef.current) && showKeyboard
            }
          )
        }
        disabled={ disabled }
        readOnly={ isMobile }
        autoComplete="off"
      />
    </>
  );
}
